import React from 'react';
import { getPatternStyle } from '../utils/patternStyle';

const PatternModal = ({ pattern, isOpen, onClose, onApplyPattern }) => {
  const copyCode = async () => {
    try {
      const codeToCopy = pattern.code || pattern.css;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(codeToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = codeToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleApplyPattern = () => {
    onApplyPattern(pattern);
  };

  const previewStyle = getPatternStyle(pattern);

  if (!isOpen || !pattern) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[1000] p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xs sm:max-w-md lg:max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-slate-900 font-semibold text-lg sm:text-xl truncate">{pattern.name}</h2>
            {pattern.badge && (
              <div className="mt-2">
                <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-xs font-medium">
                  {pattern.badge}
                </span>
              </div>
            )}
          </div>

          <button
            className="text-slate-500 hover:text-slate-800 p-2 rounded-md transition-colors"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Preview */}
        <div className="p-5 sm:p-6 border-b border-slate-200 bg-slate-50">
          <div
            className="w-full h-40 sm:h-48 md:h-56 lg:h-64 rounded-xl overflow-hidden border border-slate-200"
            style={previewStyle}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-b border-slate-200">
          <button
            className="flex-1 bg-slate-900 hover:bg-slate-950 text-white font-medium py-3 px-5 rounded-xl transition-colors text-sm sm:text-base flex items-center justify-center gap-2"
            onClick={handleApplyPattern}
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Apply to Website
          </button>
          <button
            className="flex-1 bg-white hover:bg-slate-50 text-slate-900 font-medium py-3 px-5 rounded-xl border border-slate-200 transition-colors text-sm sm:text-base flex items-center justify-center gap-2"
            onClick={copyCode}
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy Code
          </button>
        </div>

        {/* Code */}
        <div className="p-5 sm:p-6 bg-white">
          <div className="text-sm sm:text-base font-semibold text-slate-900 mb-3">CSS Code</div>
          <div className="rounded-xl border border-slate-200 bg-slate-900 overflow-x-auto">
            <pre className="text-xs sm:text-sm text-white whitespace-pre-wrap break-words font-mono leading-relaxed p-4 sm:p-5">
              {pattern.code || pattern.css}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternModal;
